import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpenCheck,
  ChevronLeft,
  ChevronRight,
  Database,
  KeyRound,
  LogOut,
  MapPin,
  MessageSquareText,
  Power,
  RefreshCw,
  Search,
  Trash2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  SandArcs,
  SectionHeader,
  Tag,
} from "../components/design-system";
import { ConfirmDialog } from "../components/ui";
import { CompanionAvatar } from "../components/companion-avatar";
import { useResource } from "../hooks/use-resource";
import {
  createLatestSelectionTracker,
  isCurrentSelection,
} from "../latest-selection";
import type {
  CourseAssistantAdminData,
  CourseAssistantAdminReview,
  CourseAssistantSharedGrade,
  GradeCourse,
  SnapshotSummary,
  UserDetail,
  UserSession,
} from "../types";
import {
  formatBytes,
  formatDateTime,
  profileEntries,
  resourceLabel,
} from "../utils";

type Confirmation =
  | {
      kind: "revoke-all" | "delete";
      userId: string;
      account: string;
    }
  | {
      kind: "delete-course-review";
      userId: string;
      account: string;
      reviewId: string;
      displayName: string;
    }
  | null;
type DetailTab =
  "status" | "sessions" | "grades" | "course-assistant" | "data" | "profile";

const PAGE_SIZE = 6;

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const detailRequests = useRef(createLatestSelectionTracker<string>());
  const actionInFlight = useRef(false);
  const users = useResource(
    () => api.users({ page, pageSize: PAGE_SIZE, q: query }),
    [page, query],
  );

  const loadDetail = useCallback(
    async (id: string, keepContent = false, openMobile = true) => {
      const preserveCurrent =
        keepContent && detailRequests.current.isSelected(id);
      const request = detailRequests.current.select(id);
      setSelectedId(id);
      setConfirmation(null);
      if (openMobile) setMobileDetail(true);
      if (!preserveCurrent) setDetail(null);
      setDetailLoading(true);
      try {
        const nextDetail = await api.user(id);
        if (detailRequests.current.isCurrent(request)) {
          setDetail(nextDetail);
        }
      } catch (error) {
        if (detailRequests.current.isCurrent(request)) {
          toast.error(
            error instanceof Error ? error.message : "用户详情读取失败",
          );
        }
      } finally {
        if (detailRequests.current.isCurrent(request)) {
          setDetailLoading(false);
        }
      }
    },
    [],
  );

  const clearSelection = useCallback(() => {
    detailRequests.current.invalidate();
    setSelectedId(null);
    setDetail(null);
    setDetailLoading(false);
    setConfirmation(null);
  }, []);

  const firstUserId = users.data?.items[0]?.id ?? null;

  useEffect(() => {
    if (!selectedId && firstUserId) {
      void loadDetail(firstUserId, false, false);
    }
  }, [firstUserId, loadDetail, selectedId]);

  useEffect(
    () => () => {
      detailRequests.current.invalidate();
    },
    [],
  );

  const currentActionTarget = () => {
    if (
      !detail ||
      !isCurrentSelection(detailRequests.current, selectedId, detail.id)
    ) {
      return null;
    }
    return detail;
  };

  const beginAction = () => {
    if (actionInFlight.current) return false;
    actionInFlight.current = true;
    setActionBusy(true);
    return true;
  };

  const finishAction = () => {
    actionInFlight.current = false;
    setActionBusy(false);
  };

  const reloadDetailIfSelected = async (id: string) => {
    if (detailRequests.current.isSelected(id)) {
      await loadDetail(id, true, false);
    }
  };

  const refreshUser = async () => {
    const target = currentActionTarget();
    if (!target || !beginAction()) return;
    try {
      const result = await api.refreshUser(target.id);
      const failed = result.resources.filter(
        (item) => item.status === "failed",
      );
      if (failed.length) toast.warning("部分用户数据刷新失败，请查看服务日志");
      else toast.success("用户低频数据已刷新");
      await Promise.all([reloadDetailIfSelected(target.id), users.refresh()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "刷新失败");
    } finally {
      finishAction();
    }
  };

  const confirmAction = async () => {
    const target = currentActionTarget();
    if (
      !target ||
      !confirmation ||
      target.id !== confirmation.userId ||
      target.account !== confirmation.account
    ) {
      setConfirmation(null);
      toast.error("所选用户已变化，请重新确认");
      return;
    }
    if (
      confirmation.kind === "delete-course-review" &&
      !target.courseAssistant.reviews.some(
        (review) => review.id === confirmation.reviewId,
      )
    ) {
      setConfirmation(null);
      toast.error("课程想法已变化，请重新确认");
      return;
    }
    if (!beginAction()) return;
    try {
      if (confirmation.kind === "revoke-all") {
        const result = await api.revokeUserSessions(target.id);
        toast.success(`已撤销 ${result.revoked} 个登录会话`);
        await reloadDetailIfSelected(target.id);
      } else if (confirmation.kind === "delete-course-review") {
        await api.deleteCourseAssistantReview(target.id, confirmation.reviewId);
        toast.success("课程想法已删除");
        await reloadDetailIfSelected(target.id);
      } else {
        await api.deleteUser(target.id, target.account);
        toast.success("用户及其持久数据已删除");
        if (detailRequests.current.isSelected(target.id)) {
          users.setData(null);
          clearSelection();
          setMobileDetail(false);
        }
        await users.refresh();
      }
      setConfirmation(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "操作失败");
    } finally {
      finishAction();
    }
  };

  const revokeSession = async (session: UserSession) => {
    const target = currentActionTarget();
    if (
      !target ||
      !target.sessions.some((item) => item.sessionId === session.sessionId) ||
      !beginAction()
    ) {
      return;
    }
    try {
      await api.revokeUserSession(target.id, session.sessionId);
      await reloadDetailIfSelected(target.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "撤销会话失败");
    } finally {
      finishAction();
    }
  };

  const clearDormBinding = async () => {
    const target = currentActionTarget();
    if (!target || !beginAction()) return;
    try {
      await api.clearElectricityBinding(target.id);
      await reloadDetailIfSelected(target.id);
      toast.success("住宿绑定已删除");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "住宿绑定删除失败");
    } finally {
      finishAction();
    }
  };

  const setAccountActive = async (active: boolean) => {
    const target = currentActionTarget();
    if (!target || target.accountActive === active || !beginAction()) return;
    try {
      const result = await api.setUserActivation(target.id, active);
      if (active) {
        toast.success("账户已启用");
      } else {
        toast.success(`账户已停用，已退出 ${result.revokedSessions} 个会话`);
      }
      await Promise.all([reloadDetailIfSelected(target.id), users.refresh()]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "账户状态更新失败");
    } finally {
      finishAction();
    }
  };

  const requestConfirmation = (kind: "revoke-all" | "delete") => {
    const target = currentActionTarget();
    if (!target || actionInFlight.current) return;
    setConfirmation({
      kind,
      userId: target.id,
      account: target.account,
    });
  };

  const requestCourseReviewDeletion = (review: CourseAssistantAdminReview) => {
    const target = currentActionTarget();
    if (
      !target ||
      actionInFlight.current ||
      !target.courseAssistant.reviews.some((item) => item.id === review.id)
    ) {
      return;
    }
    setConfirmation({
      kind: "delete-course-review",
      userId: target.id,
      account: target.account,
      reviewId: review.id,
      displayName: review.displayName,
    });
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        eyebrow="DIRECTORY"
        title="注册用户"
        desc={
          users.data
            ? `共 ${users.data.pagination.total} 位用户 · 搜索定位后点击查看详情`
            : "搜索定位后点击查看详情"
        }
        className="rise-in"
        action={
          <ActionButton
            busy={users.refreshing}
            onClick={() => void users.refresh()}
          >
            <RefreshCw size={13} />
            手动刷新数据
          </ActionButton>
        }
      />

      {users.error && (
        <ErrorState error={users.error} onRetry={() => void users.refresh()} />
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <Panel
          className={`rise-in rise-in-1 h-fit overflow-hidden ${mobileDetail ? "hidden lg:block" : ""}`}
        >
          <div className="border-b border-sand-light/70 p-4">
            <div className="user-directory-search flex items-center gap-2 rounded-xl bg-cream px-3.5 py-2.5 text-[13px]">
              <Search size={14} className="shrink-0 text-ink-faint" />
              <input
                value={query}
                onChange={(event) => {
                  users.setData(null);
                  setQuery(event.target.value);
                  setPage(1);
                  clearSelection();
                  setMobileDetail(false);
                }}
                placeholder="搜索姓名 / 学号 / 专业"
                className="w-full bg-transparent outline-none placeholder:text-ink-faint/70"
              />
            </div>
          </div>
          {users.loading && !users.data ? (
            <LoadingState rows={6} />
          ) : users.data?.items.length ? (
            <>
              <div className="divide-y divide-sand-light/60">
                {users.data.items.map((user) => {
                  const active = user.id === selectedId;
                  return (
                    <button
                      key={user.id}
                      onClick={() => void loadDetail(user.id)}
                      className={`flex w-full items-center gap-3.5 px-4 py-4 text-left transition ${active ? "bg-peach/70" : "hover:bg-cream/70"}`}
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-[15px] ${active ? "bg-ink text-sand" : "bg-cream-deep text-ink-soft"}`}
                      >
                        {user.displayName.slice(0, 1)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <strong className="block truncate text-[14px] font-medium text-ink">
                            {user.displayName}
                          </strong>
                          {!user.accountActive && (
                            <Tag tone="danger">已停用</Tag>
                          )}
                          {user.studentStatus &&
                            !/在籍|在校|正常|在读/.test(user.studentStatus) && (
                              <Tag tone="danger">{user.studentStatus}</Tag>
                            )}
                        </span>
                        <small className="mt-0.5 block truncate font-mono text-[11.5px] text-ink-faint">
                          {user.account} ·{" "}
                          {user.className || user.majorName || "班级信息暂无"}
                        </small>
                      </span>
                      <ChevronRight
                        size={15}
                        className={active ? "text-clay" : "text-ink-faint/50"}
                      />
                    </button>
                  );
                })}
              </div>
              <Pagination
                page={users.data.pagination.page}
                pages={users.data.pagination.totalPages}
                total={users.data.pagination.total}
                onChange={(nextPage) => {
                  users.setData(null);
                  setPage(nextPage);
                  clearSelection();
                  setMobileDetail(false);
                }}
                className="border-t border-sand-light/70 px-4 py-3.5"
              />
            </>
          ) : (
            <EmptyState
              title="没有匹配的用户"
              description="调整关键词后重新搜索，或等待用户首次登录。"
            />
          )}
        </Panel>

        <div className={`${mobileDetail ? "block" : "hidden lg:block"}`}>
          {mobileDetail && (
            <button
              onClick={() => setMobileDetail(false)}
              className="mb-3 flex items-center gap-1 text-[12.5px] text-ink-soft lg:hidden"
            >
              <ChevronLeft size={15} />
              返回用户列表
            </button>
          )}
          <Panel className="rise-in rise-in-2 min-h-[520px] overflow-hidden">
            {detailLoading && !detail ? (
              <LoadingState rows={10} />
            ) : detail ? (
              <UserDetailView
                detail={detail}
                busy={actionBusy}
                onRefresh={() => void refreshUser()}
                onActivationChange={(active) => void setAccountActive(active)}
                onRevoke={() => requestConfirmation("revoke-all")}
                onRevokeSession={(session) => void revokeSession(session)}
                onClearDormBinding={() => void clearDormBinding()}
                onDeleteCourseReview={requestCourseReviewDeletion}
                onDelete={() => requestConfirmation("delete")}
              />
            ) : (
              <EmptyState
                title="选择一位用户"
                description="用户资料、会话、成绩与全部持久数据将在这里集中显示。"
              />
            )}
          </Panel>
        </div>
      </div>

      <ConfirmDialog
        open={confirmation?.kind === "revoke-all"}
        title="撤销全部登录会话？"
        description="该用户所有设备上的 Bearer 会话都会立即失效，但账号和数据不会删除。"
        confirmLabel="撤销会话"
        danger
        busy={actionBusy}
        onCancel={() => setConfirmation(null)}
        onConfirm={() => void confirmAction()}
      />
      <ConfirmDialog
        open={confirmation?.kind === "delete-course-review"}
        title="删除这条课程想法？"
        description={
          confirmation?.kind === "delete-course-review"
            ? `“${confirmation.displayName}”的想法会立即从学生端删除，关联点赞也会一并清除。此操作无法撤销。`
            : ""
        }
        confirmLabel="删除想法"
        danger
        busy={actionBusy}
        onCancel={() => setConfirmation(null)}
        onConfirm={() => void confirmAction()}
      />
      <ConfirmDialog
        open={confirmation?.kind === "delete"}
        title="永久删除这个用户？"
        description="用户资料、成绩等持久快照及全部登录会话都会删除。此操作无法撤销。"
        confirmLabel="永久删除"
        danger
        requiredText={
          confirmation?.kind === "delete" ? confirmation.account : undefined
        }
        busy={actionBusy}
        onCancel={() => setConfirmation(null)}
        onConfirm={() => void confirmAction()}
      />
    </div>
  );
}

function UserDetailView({
  detail,
  busy,
  onRefresh,
  onActivationChange,
  onRevoke,
  onRevokeSession,
  onClearDormBinding,
  onDeleteCourseReview,
  onDelete,
}: {
  detail: UserDetail;
  busy: boolean;
  onRefresh: () => void;
  onActivationChange: (active: boolean) => void;
  onRevoke: () => void;
  onRevokeSession: (session: UserSession) => void;
  onClearDormBinding: () => void;
  onDeleteCourseReview: (review: CourseAssistantAdminReview) => void;
  onDelete: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>("status");
  const gradeCourses = detail.snapshots.find(
    (snapshot) => snapshot.resourceKey === "grades",
  )?.data?.courses;
  const grades = Array.isArray(gradeCourses) ? gradeCourses : [];
  const gradeGroups = useMemo(() => {
    const grouped = new Map<string, GradeCourse[]>();
    for (const course of grades) {
      const key = semesterLabel(course);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(course);
    }
    return [...grouped.entries()]
      .map(([key, courses]) => ({ key, courses }))
      .sort((left, right) =>
        right.key.localeCompare(left.key, "zh-CN", { numeric: true }),
      );
  }, [grades]);
  const semesters = gradeGroups.map((group) => group.key);
  const [semester, setSemester] = useState("all");
  useEffect(() => setSemester("all"), [detail.id]);
  const visibleGradeGroups =
    semester === "all"
      ? gradeGroups
      : gradeGroups.filter((group) => group.key === semester);
  const allGradeSummary = useMemo(() => summarizeGrades(grades), [grades]);
  const electricityBinding = record(
    detail.snapshots.find((snapshot) => snapshot.resourceKey === "electricity")
      ?.data?.binding,
  );
  const dormBindingName = [
    text(electricityBinding.buildingName),
    text(electricityBinding.roomNumber),
  ]
    .filter(Boolean)
    .join("");
  const dormBindingWaitDays = daysUntilDormBindingChange(
    text(electricityBinding.changedAt),
    text(electricityBinding.boundAt),
  );
  const majorName = text(detail.profile.majorName) || "专业暂未记录";
  const tabs: Array<{ key: DetailTab; label: string }> = [
    { key: "status", label: "状态" },
    { key: "sessions", label: `会话 ${detail.sessions.length}` },
    { key: "grades", label: `成绩 ${grades.length}` },
    { key: "course-assistant", label: "选课助手" },
    { key: "data", label: `数据 ${detail.snapshots.length}` },
    { key: "profile", label: "资料" },
  ];

  return (
    <>
      <div className="relative overflow-hidden border-b border-sand-light/70 bg-gradient-to-br from-peach via-paper to-cream px-6 py-6 sm:px-8">
        <SandArcs className="absolute -right-28 -top-20 w-[420px] opacity-70" />
        <div className="relative flex flex-wrap items-start gap-4">
          {detail.companion?.selected ? (
            <CompanionAvatar
              companion={detail.companion}
              className="h-14 w-14 shrink-0"
            />
          ) : (
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-ink font-display text-xl text-sand">
              {detail.displayName.slice(0, 1)}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-[9px] text-clay">STUDENT PROFILE</p>
            <h3 className="mt-1 font-display text-xl font-medium text-ink">
              {detail.displayName}
            </h3>
            <p className="mt-1 font-mono text-[11.5px] text-ink-faint">
              {detail.account} · {majorName}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton busy={busy} onClick={onRefresh}>
              <RefreshCw size={13} />
              刷新数据
            </ActionButton>
            <ActionButton onClick={onRevoke} disabled={busy}>
              <KeyRound size={13} />
              撤销全部
            </ActionButton>
            <div className="flex h-9 items-center gap-2 rounded-full border border-sand-light bg-paper/80 px-3 text-[11.5px] font-medium text-ink-soft shadow-sm backdrop-blur-sm">
              <span>{detail.accountActive ? "账户已启用" : "账户已停用"}</span>
              <button
                type="button"
                role="switch"
                aria-checked={detail.accountActive}
                aria-label={detail.accountActive ? "停用账户" : "启用账户"}
                disabled={busy}
                onClick={() => onActivationChange(!detail.accountActive)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${detail.accountActive ? "bg-sage" : "bg-sand"}`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-paper shadow-sm transition-all ${detail.accountActive ? "left-[18px]" : "left-0.5"}`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="scrollbar-thin flex overflow-x-auto border-b border-sand-light/70 px-4 sm:px-6">
        {tabs.map((item) => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`relative shrink-0 px-4 py-3.5 text-[12.5px] font-medium transition ${tab === item.key ? "text-clay-deep" : "text-ink-faint hover:text-ink"}`}
          >
            {item.label}
            {tab === item.key && (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-clay" />
            )}
          </button>
        ))}
      </div>

      <div className="p-5 sm:p-7">
        {tab === "status" && (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <StatusCard
                icon={Power}
                label="账户状态"
                value={detail.accountActive ? "已启用" : "已停用"}
                hint={accountStateHint(detail)}
              />
              <StatusCard
                icon={KeyRound}
                label="活动会话"
                value={`${detail.sessions.length} 个`}
                hint="90 天滑动有效期"
              />
              <StatusCard
                icon={Database}
                label="持久快照"
                value={`${detail.snapshots.length} 份`}
                hint="按用户隔离存储"
              />
              <StatusCard
                icon={BookOpenCheck}
                label="成绩记录"
                value={`${grades.length} 门`}
                hint="保留文字状态与细分"
              />
            </div>
            <dl className="grid gap-x-8 gap-y-0 rounded-xl border border-sand-light/70 bg-cream/40 px-5 sm:grid-cols-2">
              {[
                ["最近上线", formatDateTime(detail.lastActiveAt)],
                ["最近登录", formatDateTime(detail.lastLoginAt)],
                [
                  "停用时间",
                  detail.accountActive
                    ? "—"
                    : formatDateTime(detail.accountDeactivatedAt),
                ],
                ["资料更新", formatDateTime(detail.profileUpdatedAt)],
                ["注册时间", formatDateTime(detail.createdAt)],
                ["记录更新", formatDateTime(detail.updatedAt)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-baseline justify-between gap-4 border-b border-dashed border-sand-light/70 py-3 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0"
                >
                  <dt className="text-[12px] text-ink-faint">{label}</dt>
                  <dd className="text-right text-[12.5px] font-medium text-ink">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {tab === "sessions" &&
          (detail.sessions.length ? (
            <div className="space-y-2">
              {detail.sessions.map((session) => (
                <div
                  key={session.sessionId}
                  className="flex items-center gap-2.5 rounded-xl border border-sand-light/70 bg-cream/35 px-3 py-2.5"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-peach text-clay-deep">
                    <KeyRound size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <strong className="block truncate font-mono text-[11px] font-medium text-ink">
                      {session.id}
                    </strong>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ink-soft">
                      <MapPin size={11} className="shrink-0 text-clay" />
                      <span className="truncate">
                        最近上线 IP：{session.ipAddress || "未知 IP"} ·{" "}
                        {session.location || "未知地区"} · 最近上线时间：
                        {formatDateTime(session.lastActiveAt)}
                      </span>
                    </span>
                  </div>
                  <span className="shrink-0">
                    <Tag tone="sage">
                      到期 {formatDateTime(session.expiresAt, true)}
                    </Tag>
                  </span>
                  <button
                    type="button"
                    title="撤销此会话"
                    aria-label={`撤销会话 ${session.id}`}
                    disabled={busy}
                    onClick={() => onRevokeSession(session)}
                    className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2 text-[10.5px] font-medium text-destructive transition hover:bg-[#F3DDD3] active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <LogOut size={11} />
                    撤销
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="没有有效会话"
              description="该用户当前没有有效的小程序登录会话。"
            />
          ))}

        {tab === "grades" &&
          (grades.length ? (
            <div>
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setSemester("all")}
                  className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${semester === "all" ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
                >
                  全部学期
                </button>
                {semesters.map((item) => (
                  <button
                    key={item}
                    onClick={() => setSemester(item)}
                    className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition active:scale-95 ${semester === item ? "border-ink bg-ink text-paper" : "border-sand-light bg-paper text-ink-soft hover:border-sand"}`}
                  >
                    {item}
                  </button>
                ))}
              </div>
              {semester === "all" && (
                <GradeAggregatePanel summary={allGradeSummary} />
              )}
              <div className="space-y-7">
                {visibleGradeGroups.map((group, index) => (
                  <GradeSemesterSection
                    key={group.key}
                    label={group.key}
                    courses={group.courses}
                    separated={semester === "all" && index > 0}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              title="尚无成绩快照"
              description="该用户首次查询成绩后，完整课程与细分成绩会显示在这里。"
            />
          ))}

        {tab === "course-assistant" && (
          <CourseAssistantPanel
            data={detail.courseAssistant}
            busy={busy}
            onDeleteReview={onDeleteCourseReview}
          />
        )}

        {tab === "data" &&
          (detail.snapshots.length ? (
            <div className="space-y-3">
              {detail.snapshots.map((snapshot) => (
                <details
                  key={snapshot.resourceKey}
                  className="group overflow-hidden rounded-xl border border-sand-light/70 bg-cream/30"
                >
                  <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-peach text-[13px] font-medium text-clay-deep">
                      {resourceLabel(snapshot.resourceKey).slice(0, 1)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <strong className="block text-[13px] font-medium">
                        {resourceLabel(snapshot.resourceKey)}
                      </strong>
                      <small className="mt-0.5 block text-[11px] text-ink-faint">
                        v{snapshot.schemaVersion} ·{" "}
                        {formatBytes(snapshot.payloadBytes)} ·{" "}
                        {formatDateTime(snapshot.fetchedAt, true)}
                      </small>
                    </span>
                    <span className="text-[11px] text-ink-faint group-open:text-clay-deep">
                      展开
                    </span>
                  </summary>
                  <SnapshotContent snapshot={snapshot} />
                </details>
              ))}
            </div>
          ) : (
            <EmptyState
              title="尚无持久数据"
              description="成绩、课表等低频资源产生后会显示在这里。"
            />
          ))}

        {tab === "profile" && (
          <dl className="grid grid-cols-2 gap-x-8 gap-y-0 xl:grid-cols-3">
            <div className="min-w-0 border-b border-dashed border-sand-light/70 py-3">
              <dt className="flex items-center gap-1.5 text-[11.5px] text-ink-faint">
                <span>绑定住宿点 ({dormBindingWaitDays})</span>
                {dormBindingName && (
                  <button
                    type="button"
                    title="删除住宿绑定"
                    aria-label="删除住宿绑定"
                    disabled={busy}
                    onClick={onClearDormBinding}
                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-ink-faint transition hover:bg-peach hover:text-clay-deep active:scale-90 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <RefreshCw
                      size={11}
                      className={busy ? "animate-spin" : ""}
                    />
                  </button>
                )}
              </dt>
              <dd className="mt-1 break-words text-[13.5px] font-medium text-ink">
                {dormBindingName || "未绑定"}
              </dd>
            </div>
            {profileEntries(detail.profile).map((entry) => (
              <div
                key={entry.key}
                className="min-w-0 border-b border-dashed border-sand-light/70 py-3"
              >
                <dt className="text-[11.5px] text-ink-faint">{entry.label}</dt>
                <dd className="mt-1 break-words text-[13.5px] font-medium text-ink">
                  {entry.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-sand-light/70 pt-5">
          <div>
            <p className="text-[12.5px] font-medium text-ink">危险操作</p>
            <p className="mt-0.5 text-[11px] text-ink-faint">
              删除会同时移除全部快照与登录会话。
            </p>
          </div>
          <ActionButton tone="danger" onClick={onDelete} disabled={busy}>
            <Trash2 size={13} />
            删除用户
          </ActionButton>
        </div>
      </div>
    </>
  );
}

function CourseAssistantPanel({
  data,
  busy,
  onDeleteReview,
}: {
  data: CourseAssistantAdminData;
  busy: boolean;
  onDeleteReview: (review: CourseAssistantAdminReview) => void;
}) {
  const courseCount = new Set(data.sharedGrades.map((grade) => grade.courseKey))
    .size;
  const physicalEducationCount = new Set(
    data.sharedGrades
      .filter((grade) => grade.type === "physical_education")
      .map((grade) => grade.courseKey),
  ).size;

  return (
    <div className="space-y-7">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatusCard
          icon={BookOpenCheck}
          label="共享课程"
          value={`${courseCount} 门`}
          hint={`${data.sharedGrades.length} 条成绩记录`}
        />
        <StatusCard
          icon={Power}
          label="其中体育课"
          value={`${physicalEducationCount} 门`}
          hint="括号内项目保留原始字符"
        />
        <StatusCard
          icon={MessageSquareText}
          label="已发想法"
          value={`${data.reviews.length} 条`}
          hint="学生端以匿名身份展示"
        />
      </div>

      <section>
        <SectionHeader
          eyebrow="SHARED GRADES"
          title="共享的课程成绩"
          desc="仅包含通识选修课与体育课；同一课程的历次成绩分别列出。"
        />
        {data.sharedGrades.length ? (
          <div className="scrollbar-thin mt-4 overflow-x-auto rounded-xl border border-sand-light/70">
            <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-sand-light bg-cream/50 text-left text-[11px] text-ink-faint">
                  {[
                    "课程",
                    "类型",
                    "学期",
                    "教师",
                    "学分",
                    "成绩",
                    "同步时间",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className="whitespace-nowrap px-4 py-3 font-medium"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.sharedGrades.map((grade) => (
                  <tr
                    key={grade.attemptKey}
                    className="border-b border-sand-light/70 last:border-b-0 hover:bg-cream/35"
                  >
                    <td className="max-w-[240px] px-4 py-3.5">
                      <strong className="block break-words font-medium text-ink">
                        {grade.displayName}
                      </strong>
                      {grade.displayName !== grade.courseName && (
                        <small className="mt-0.5 block break-words font-mono text-[10.5px] text-ink-faint">
                          {grade.courseName}
                        </small>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5">
                      <Tag
                        tone={
                          grade.type === "physical_education" ? "sage" : "clay"
                        }
                      >
                        {grade.type === "physical_education"
                          ? "体育课程"
                          : "通识选修"}
                      </Tag>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 font-mono text-[11px] text-ink-soft">
                      {grade.termLabel}
                    </td>
                    <td className="max-w-[160px] px-4 py-3.5 text-ink-soft">
                      {grade.teacherName || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 font-serif-num text-ink-soft">
                      {grade.credits ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 font-serif-num text-[16px] font-medium text-clay-deep">
                      {courseAssistantGradeScore(grade)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-[11px] text-ink-faint">
                      {formatDateTime(grade.sourceFetchedAt, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4">
            <EmptyState
              title="暂无可共享成绩"
              description="成绩中暂未识别到通识选修课或体育课。"
            />
          </div>
        )}
      </section>

      <section>
        <SectionHeader
          eyebrow="ANONYMOUS THOUGHTS"
          title="发布的课程想法"
          desc="管理员可在所选用户下核对课程、成绩与该用户发布的内容。"
        />
        {data.reviews.length ? (
          <div className="mt-4 space-y-3">
            {data.reviews.map((review) => (
              <article
                key={review.id}
                className="rounded-xl border border-sand-light/70 bg-paper p-4"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-peach text-clay-deep">
                    <MessageSquareText size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="text-[13.5px] font-medium text-ink">
                        {review.displayName}
                      </strong>
                      <Tag tone="neutral">{review.termLabel}</Tag>
                      <span className="text-[11px] text-ink-faint">
                        {review.teacherNames?.length
                          ? review.teacherNames.join("、")
                          : "暂无教师信息"}
                      </span>
                    </div>
                    {review.displayName !== review.courseName && (
                      <p className="mt-0.5 break-words font-mono text-[10.5px] text-ink-faint">
                        {review.courseName}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-serif-num text-[17px] font-medium text-clay-deep">
                      {review.rating.toFixed(1)} / 5
                    </p>
                    <p className="mt-0.5 text-[10.5px] text-ink-faint">
                      成绩 {formatGradeMetric(review.calculationScore, 1)}
                    </p>
                  </div>
                </div>
                {review.keywords.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {review.keywords.map((keyword) => (
                      <Tag
                        key={keyword.text}
                        tone={
                          keyword.sentiment === "positive"
                            ? "clay"
                            : keyword.sentiment === "negative"
                              ? "danger"
                              : "sage"
                        }
                      >
                        {keyword.text}
                      </Tag>
                    ))}
                  </div>
                )}
                <p className="mt-3 whitespace-pre-wrap break-words text-[13px] leading-6 text-ink-soft">
                  {review.content}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-dashed border-sand-light/70 pt-3 text-[10.5px] text-ink-faint">
                  <span>获赞 {review.likeCount} 次</span>
                  <span>{formatDateTime(review.createdAt, true)}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDeleteReview(review)}
                    className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[10.5px] font-medium text-destructive transition hover:bg-[#F3DDD3] active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Trash2 size={11} />
                    删除想法
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-4">
            <EmptyState
              title="尚未发布想法"
              description="课程想法为可选项，该用户目前只共享成绩。"
            />
          </div>
        )}
      </section>
    </div>
  );
}

function courseAssistantGradeScore(grade: CourseAssistantSharedGrade): string {
  if (grade.finalScore !== null && grade.finalScore !== "") {
    return String(grade.finalScore);
  }
  return formatGradeMetric(grade.calculationScore, 1);
}

function SnapshotContent({ snapshot }: { snapshot: SnapshotSummary }) {
  const data = snapshot.data;
  if (!data) {
    return (
      <p className="border-t border-sand-light/70 px-4 py-5 text-[12px] text-ink-faint">
        这份快照没有可展示的数据。
      </p>
    );
  }
  if (snapshot.resourceKey === "grades") {
    const courses = Array.isArray(data.courses) ? data.courses : [];
    return (
      <SnapshotNote
        title={`${courses.length} 门课程`}
        description="完整总评、文字状态和成绩分项已整理在上方“成绩”标签页。"
      />
    );
  }
  if (snapshot.resourceKey.startsWith("timetable:")) {
    const courses = records(data.courses);
    const semester = record(data.semester);
    return (
      <SnapshotList
        title={`${text(semester.label) || "学期未知"} · ${courses.length} 门课程`}
        items={courses.map((course) => ({
          title: text(course.courseName) || "未命名课程",
          detail: [
            stringList(course.teacherNames).join("、"),
            `${records(course.arrangements).length} 段上课安排`,
          ]
            .filter(Boolean)
            .join(" · "),
        }))}
      />
    );
  }
  if (snapshot.resourceKey === "exams:catalog") {
    return (
      <SnapshotList
        title="本人可用考试学期"
        items={records(data.semesters).map((semester) => ({
          title: text(semester.label) || text(semester.id) || "未知学期",
          detail: `学期标识 ${text(semester.id) || "—"}`,
        }))}
      />
    );
  }
  if (snapshot.resourceKey.startsWith("exams:")) {
    return (
      <SnapshotList
        title={`${records(data.exams).length} 场考试`}
        items={records(data.exams).map((exam) => {
          const course = record(exam.course);
          const time = record(exam.time);
          const location = record(exam.location);
          return {
            title: text(course.name) || "未命名考试",
            detail: [
              text(exam.arrangementTypeLabel),
              text(time.raw),
              text(location.room),
              text(exam.seatNumber) ? `座位 ${text(exam.seatNumber)}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
          };
        })}
      />
    );
  }
  if (snapshot.resourceKey === "electricity") {
    const binding = record(data.binding);
    const account = record(data.account);
    return (
      <div className="grid gap-x-7 border-t border-sand-light/70 bg-cream/30 px-4 py-3 sm:grid-cols-2">
        {[
          [
            "绑定寝室",
            [text(binding.buildingName), text(binding.roomNumber)]
              .filter(Boolean)
              .join(" · ") || "未绑定",
          ],
          ["剩余金额", numberLabel(account.remainingAmountYuan, "元")],
          ["计费电量", numberLabel(account.billedElectricityKwh, "度")],
          ["累计电费", numberLabel(account.electricityFeeYuan, "元")],
          ["上次缴费", text(account.lastPaymentDate) || "暂无记录"],
          ["最后结算", text(account.lastSettlementDate) || "暂无记录"],
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-4 border-b border-dashed border-sand-light/70 py-2.5"
          >
            <span className="text-[11px] text-ink-faint">{label}</span>
            <strong className="text-right text-[12.5px] font-medium text-ink">
              {value}
            </strong>
          </div>
        ))}
      </div>
    );
  }
  if (snapshot.resourceKey === "schedule") {
    return (
      <SnapshotList
        title={`${records(data.plans).length} 条本地日程 · 本地状态优先`}
        items={records(data.plans).map((plan) => ({
          title: `${text(plan.done) === "true" ? "已完成 · " : ""}${text(plan.title) || "未命名日程"}`,
          detail: `${text(plan.date)} ${text(plan.startTime)}–${text(plan.endDate)} ${text(plan.endTime)}`,
        }))}
      />
    );
  }
  if (snapshot.resourceKey === "messages") {
    return (
      <SnapshotList
        title={`最近 ${records(data.items).length} 条教务消息`}
        items={records(data.items).map((item) => ({
          title:
            text(item.courseName) ||
            text(item.title) ||
            text(item.content) ||
            "教务消息",
          detail: [text(item.createdAt), text(item.type)]
            .filter(Boolean)
            .join(" · "),
        }))}
      />
    );
  }
  return (
    <pre className="scrollbar-thin max-h-96 overflow-auto border-t border-sand-light/70 bg-[#2E2A25] p-4 text-[11px] leading-relaxed text-[#F3EDE3]">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function SnapshotList({
  title,
  items,
}: {
  title: string;
  items: Array<{ title: string; detail: string }>;
}) {
  return (
    <div className="border-t border-sand-light/70 bg-cream/25">
      <p className="border-b border-sand-light/60 px-4 py-2.5 text-[11.5px] font-medium text-ink-soft">
        {title}
      </p>
      {items.length ? (
        <div className="scrollbar-thin max-h-96 divide-y divide-dashed divide-sand-light/70 overflow-y-auto px-4">
          {items.map((item, index) => (
            <div key={`${item.title}:${index}`} className="py-2.5">
              <p className="text-[12.5px] font-medium text-ink">{item.title}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-ink-faint">
                {item.detail || "暂无更多信息"}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="px-4 py-5 text-[12px] text-ink-faint">暂无记录</p>
      )}
    </div>
  );
}

function SnapshotNote({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="border-t border-sand-light/70 bg-cream/30 px-4 py-4">
      <p className="text-[13px] font-medium text-ink">{title}</p>
      <p className="mt-1 text-[11.5px] text-ink-faint">{description}</p>
    </div>
  );
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function accountStateHint(detail: UserDetail): string {
  if (detail.accountActive) return "允许用户登录";
  return detail.accountDeactivationReason === "session_limit"
    ? "会话数达到 15 个后自动停用"
    : "由管理员停用";
}

function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.map(record) : [];
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(text).filter(Boolean) : [];
}

function text(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function daysUntilDormBindingChange(
  changedAt: string,
  boundAt: string,
): number {
  const reference = new Date(changedAt || boundAt);
  if (Number.isNaN(reference.getTime())) return 0;
  const remaining = reference.getTime() + 30 * 86_400_000 - Date.now();
  return Math.max(0, Math.ceil(remaining / 86_400_000));
}

function numberLabel(value: unknown, unit: string): string {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(2)} ${unit}` : "暂无记录";
}

function StatusCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof UserRound;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border border-sand-light/70 bg-paper p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] text-ink-soft">{label}</span>
        <Icon size={14} className="text-clay" />
      </div>
      <strong className="mt-3 block font-serif-num text-xl font-medium text-ink">
        {value}
      </strong>
      <small className="mt-1 block text-[10.5px] text-ink-faint">{hint}</small>
    </div>
  );
}

function GradeSemesterSection({
  label,
  courses,
  separated,
}: {
  label: string;
  courses: GradeCourse[];
  separated: boolean;
}) {
  const summary = summarizeGrades(courses);

  return (
    <section
      className={separated ? "border-t border-sand-light/80 pt-7" : undefined}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow text-[9px] text-clay">SEMESTER</p>
          <h4 className="mt-1 font-display text-[17px] font-medium text-ink">
            {label}
          </h4>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-[11.5px] text-ink-faint">
          <span>
            总学分{" "}
            <b className="font-serif-num text-[13px] font-medium text-ink">
              {formatGradeMetric(summary.totalCredits)}
            </b>
          </span>
          <span>
            加权平均分{" "}
            <b className="font-serif-num text-[13px] font-medium text-clay-deep">
              {formatGradeMetric(summary.weightedAverage)}
            </b>
          </span>
          <span>
            绩点{" "}
            <b className="font-serif-num text-[13px] font-medium text-ink">
              {formatGradeMetric(summary.gradePointAverage)}
            </b>
          </span>
          <span>{summary.courseCount} 门课程</span>
        </div>
      </div>

      <div className="scrollbar-thin mt-3 overflow-x-auto">
        <table className="w-full min-w-[680px] border-collapse text-[12.5px]">
          <thead>
            <tr className="border-b border-sand-light text-left text-[11px] uppercase tracking-wider text-ink-faint">
              {["课程名称", "课程号", "学分", "开课单位", "教学班"].map(
                (heading) => (
                  <th
                    key={heading}
                    className="whitespace-nowrap px-3 py-3 font-medium"
                  >
                    {heading}
                  </th>
                ),
              )}
            </tr>
          </thead>
          {courses.map((course, index) => (
            <tbody
              key={`${course.id || course.courseCode}:${index}`}
              className="border-b border-sand-light/70 last:border-b-0"
            >
              <tr className="transition hover:bg-cream/60">
                <td className="px-3 py-3.5 font-medium text-ink">
                  {course.courseName}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 font-mono text-[11.5px] text-ink-soft">
                  {course.courseCode || "—"}
                </td>
                <td className="px-3 py-3.5">{course.credits ?? "—"}</td>
                <td
                  className="whitespace-nowrap px-3 py-3.5 text-ink-soft"
                  title={course.department || undefined}
                >
                  {compactGradeColumn(course.department)}
                </td>
                <td
                  className="whitespace-nowrap px-3 py-3.5 text-ink-soft"
                  title={course.teachingClass || undefined}
                >
                  {compactGradeColumn(course.teachingClass)}
                </td>
              </tr>
              <tr className="bg-cream/35">
                <td colSpan={5} className="px-3 py-2.5">
                  <div className="flex items-center gap-4">
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px]">
                      {(course.components || []).map(
                        (component, componentIndex) => (
                          <span
                            key={`${component.name}:${componentIndex}`}
                            className="inline-flex items-baseline gap-1.5"
                          >
                            <span className="text-ink-faint">
                              {component.name}
                              {component.weightPercent !== null &&
                              component.weightPercent !== undefined
                                ? ` ${component.weightPercent}%`
                                : ""}
                            </span>
                            <b className="font-serif-num text-[12.5px] font-medium text-ink">
                              {component.score ?? "—"}
                            </b>
                          </span>
                        ),
                      )}
                      {course.gradeRemark && (
                        <span className="inline-flex items-baseline gap-1.5">
                          <span className="text-ink-faint">备注</span>
                          <b className="font-medium text-clay-deep">
                            {course.gradeRemark}
                          </b>
                        </span>
                      )}
                      {!course.components?.length && !course.gradeRemark && (
                        <span className="text-ink-faint">暂无分项成绩</span>
                      )}
                    </div>
                    <span className="flex shrink-0 items-baseline gap-3 border-l border-sand-light/80 pl-4 text-[11.5px] text-ink-faint">
                      <span>
                        总分{" "}
                        <b
                          className={`ml-1 font-serif-num text-[15px] font-medium ${typeof course.finalScore === "number" && course.finalScore >= 90 ? "text-clay-deep" : "text-ink"}`}
                        >
                          {course.finalScore ?? "—"}
                        </b>
                      </span>
                      <span>
                        绩点{" "}
                        <b className="ml-1 font-serif-num text-[14px] font-medium text-ink">
                          {typeof course.gradePoint === "number"
                            ? course.gradePoint.toFixed(1)
                            : "—"}
                        </b>
                      </span>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}

interface GradeAggregate {
  courseCount: number;
  totalCredits: number;
  weightedAverage: number | null;
  gradePointAverage: number | null;
}

function summarizeGrades(courses: GradeCourse[]): GradeAggregate {
  const totalCredits = courses.reduce(
    (total, course) =>
      total + (typeof course.credits === "number" ? course.credits : 0),
    0,
  );
  const weightedCourses = courses.filter(
    (course) =>
      typeof course.credits === "number" &&
      course.credits > 0 &&
      (typeof course.calculationScore === "number" ||
        typeof course.finalScore === "number"),
  );
  const weightedCredits = weightedCourses.reduce(
    (total, course) => total + Number(course.credits),
    0,
  );
  const weightedTotal = weightedCourses.reduce(
    (total, course) =>
      total +
      Number(course.credits) *
        Number(
          typeof course.calculationScore === "number"
            ? course.calculationScore
            : course.finalScore,
        ),
    0,
  );
  const gradePointCourses = courses.filter(
    (course) =>
      typeof course.credits === "number" &&
      course.credits > 0 &&
      typeof course.gradePoint === "number",
  );
  const gradePointCredits = gradePointCourses.reduce(
    (total, course) => total + Number(course.credits),
    0,
  );
  const gradePointTotal = gradePointCourses.reduce(
    (total, course) =>
      total + Number(course.credits) * Number(course.gradePoint),
    0,
  );
  return {
    courseCount: courses.length,
    totalCredits: Number(totalCredits.toFixed(2)),
    weightedAverage: weightedCredits ? weightedTotal / weightedCredits : null,
    gradePointAverage: gradePointCredits
      ? gradePointTotal / gradePointCredits
      : null,
  };
}

function GradeAggregatePanel({ summary }: { summary: GradeAggregate }) {
  const metrics = [
    ["总学分", formatGradeMetric(summary.totalCredits)],
    ["加权平均分", formatGradeMetric(summary.weightedAverage)],
    ["绩点", formatGradeMetric(summary.gradePointAverage)],
    ["课程门数", `${summary.courseCount} 门`],
  ];
  return (
    <div className="mb-7 overflow-hidden rounded-2xl border border-sand-light/80 bg-gradient-to-br from-cream/80 to-paper px-5 py-4">
      <p className="eyebrow text-[9px] text-clay">全部学期汇总</p>
      <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label}>
            <span className="block text-[10.5px] text-ink-faint">{label}</span>
            <strong className="mt-1 block font-serif-num text-[19px] font-medium text-ink">
              {value}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatGradeMetric(value: number | null, digits = 2) {
  if (value === null || !Number.isFinite(value)) return "—";
  return Number.isInteger(value) ? String(value) : value.toFixed(digits);
}

function semesterLabel(course: GradeCourse) {
  return `${course.academicYear} · ${course.term ? `第 ${course.term} 学期` : "学期未知"}`;
}

function compactGradeColumn(value: string) {
  const normalized = value.trim();
  if (!normalized) return "—";
  const characters = Array.from(normalized);
  return characters.length > 8
    ? `${characters.slice(0, 7).join("")}...`
    : normalized;
}
